/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	type CSSProperties,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { cssMap, cx, jsx } from '@compiled/react';

import type { StrictXCSSProp } from '@atlaskit/css';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import { useMotion, Reanimate } from '@atlaskit/motion/use-motion';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { useSkipLinkInternal } from '../../context/skip-links/use-skip-link-internal';

import {
	contentHeightWhenFixed,
	contentInsetBlockStart,
	localSlotLayers,
	panelPanelSplitterId,
	panelVar,
	sideNavLiveWidthVar,
	UNSAFE_panelLayoutVar,
} from './constants';
import { DangerouslyHoistCssVarToDocumentRoot } from './dangerously-hoist-css-var-to-document-root';
import { DangerouslyHoistSlotSizes } from './hoist-slot-sizes-context';
import { PanelSplitterProvider } from './panel-splitter/provider';
import type { ResizeBound, ResizeBounds } from './panel-splitter/types';
import { useSideNavRef } from './side-nav/use-side-nav-ref';
import type { CommonSlotProps } from './types';
import { useLayoutId } from './use-layout-id';
import { useResizingWidthCssVarOnRootElement } from './use-resizing-width-css-var-on-root-element';
import { useSafeDefaultWidth } from './use-safe-default-width';

const panelSplitterResizingVar = '--n_pnlRsz';

const fallbackDefaultWidth = 365;

/**
 * We typically use the `defaultWidth` as the minimum resizing width,
 * but for large default widths we fallback to a standard value.
 *
 * This standard value of `400px` is to align with the Global Preview Panels.
 */
const fallbackResizeMinWidth = 400;

const styles = cssMap({
	root: {
		gridArea: 'main / aside / aside / aside',
		justifySelf: 'end',
		boxSizing: 'border-box',
		// On small viewports the panel is displayed above other slots so we set its zindex.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
		zIndex: localSlotLayers.panelSmallViewports,
		// Height is set so it takes up all of the available viewport space minus top bar + banner.
		// Since panel is always rendered ontop of other grid items across all viewports height is
		// always set.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		height: contentHeightWhenFixed,
		position: 'sticky',
		// This sets the sticky point to be just below top bar + banner. It's needed to ensure the stick
		// point is exactly where this element is rendered to with no wiggle room. Unfortunately the CSS
		// spec for sticky doesn't support "stick to where I'm initially rendered" so we need to tell it.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		insetBlockStart: contentInsetBlockStart,
		backgroundColor: token('elevation.surface.overlay'),
		/**
		 * For mobile viewports, the panel will try to take the minimum width, but no larger than 90% of the screen width.
		 *
		 * The minimum width is derived from the default width of the panel.
		 *
		 * This ensures the panel:
		 *
		 * - only shrinks below its minimum width if the viewport is too small
		 * - is not forced to grow if it is smaller than `365px`
		 */
		width: `min(90%, var(--minWidth))`,
		'@media (min-width: 48rem)': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			width: `var(${panelSplitterResizingVar}, var(${panelVar}))`,
		},
		'@media (min-width: 64rem)': {
			backgroundColor: token('elevation.surface'),
		},
		'@media (min-width: 90rem)': {
			gridArea: 'panel',
			// On large viewports the panel is displayed next to other slots so we reset the zindex.
			zIndex: 'auto',
		},
	},
	border: {
		// We use a shadow when the panel is an overlay
		boxShadow: token('elevation.shadow.overlay'),
		// Not required, but declaring explicitly because we really don't want a border at small sizes
		// Previously we had a transparent border to maintain width, but this unintentionally acted as padding
		borderInlineStart: 'none',
		'@media (min-width: 64rem)': {
			// Hiding the box shadow because we are adding a border
			boxShadow: 'none',
			// We only want the border to be visible when it is not an overlay
			borderInlineStart: `${token('border.width')} solid ${token('color.border')}`,
		},
	},
	scrollContainer: {
		overflow: 'auto',
		height: '100%',
	},
	hidden: {
		/**
		 * TODO: remove this once the `width: 0` usage is removed from Jira.
		 * https://jplat.atlassian.net/browse/BLU-3951
		 *
		 * This is used to hide the panel when the `defaultWidth` is set to 0.
		 * To ensure a usable experience on small viewports, we're using `display: none` to hide the slot, instead of just using the `defaultWidth`
		 * value, so that small viewports still use our pre-defined static size. e.g. if the slot was resized to `150px`, we still want the width
		 * on small viewports to be our static value.
		 */
		display: 'none',
	},
	entering: {
		animation: token('motion.panel.enter.right'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		"[dir='rtl'] &": {
			animation: token('motion.panel.enter.left'),
		},
	},
	exiting: {
		animation: token('motion.panel.exit.right'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		"[dir='rtl'] &": {
			animation: token('motion.panel.exit.left'),
		},
	},
	contentEntering: {
		animation: token('motion.panel.content.enter'),
	},
	contentExiting: {
		animation: token('motion.panel.content.exit'),
	},
});

/**
 * The Panel layout area is rendered to the right (inline end) of the Main area, or the Aside area if it is present.
 *
 * On small viewports (below 64rem, or 1024px), the Panel slot will become an overlay.
 *
 * You can optionally render a `PanelSplitter` as a child to make the panel area resizable.
 */
export function Panel({
	children,
	defaultWidth: defaultWidthProp = fallbackDefaultWidth,
	label = 'Panel',
	skipLinkLabel = label,
	testId,
	id: providedId,
	xcss,
	hasBorder = true,
	maxWidth,
}: CommonSlotProps & {
	/**
	 * The content of the layout area.
	 */
	children: React.ReactNode;
	/**
	 * The accessible name of the slot, announced by screen readers.
	 */
	label?: string;
	/**
	 * The default width of the layout area.
	 *
	 * It should be an integer between the resize bounds - the minimum is 120px and the maximum is 50% of the viewport width.
	 *
	 * This value is also used as the minimum resizing width, except when the `defaultWidth` is greater then `400px`,
	 * in which case `400px` will be used as the minimum resizing width instead.
	 */
	defaultWidth?: number;
	/**
	 * Opt-in override for the maximum width the panel can be resized to, applied to both the rendered
	 * width and the resize bounds (mouse and keyboard).
	 *
	 * When omitted, the panel defaults to half the viewport width (after removing the sidebar width),
	 * which keeps it from growing larger than the page content. Provide a `vw` or `px` value (e.g.
	 * `'70vw'`) to allow a wider panel.
	 *
	 * This is currently used by Jira's Issue Navigator native issue preview panel; other consumers
	 * should leave it unset to keep the default cap.
	 */
	maxWidth?: ResizeBound;
	/**
	 * Bounded style overrides.
	 */
	xcss?: StrictXCSSProp<'backgroundColor', never>;
	// We can clean up AFM usage
	// eslint-disable-next-line @repo/internal/deprecations/deprecation-ticket-required
	/**
	 * Whether or not the slot has its own border. On small screens this becomes a shadow.
	 *
	 * Defaults to true. Will be removed in the future (and disabling the border will not be supported).
	 *
	 * @deprecated
	 */
	hasBorder?: boolean;
}): JSX.Element {
	const dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);
	const id = useLayoutId({ providedId });

	const isMotionUpliftEnabled = fg('platform-dst-motion-uplift-panel');

	const defaultWidth = useSafeDefaultWidth({
		defaultWidthProp,
		fallbackDefaultWidth,
		slotName: 'Panel',
	});

	// useMotion is applied only when the `platform-dst-motion-uplift-panel` gate is enabled.
	const {
		state,
		ref: motionRef,
		reanimate,
	} = useMotion<HTMLElement>({
		onFinish: (state) => {
			if (state === 'exiting' && defaultWidthRef.current === 0) {
				// Set width to 0 as animation is complete
				setWidth(0);
			}
		},
		initialState: defaultWidth === 0 ? 'hidden' : undefined,
	});

	/**
	 * Don't show the skip link if the slot has 0 width.
	 *
	 * Remove `isHidden` usage after https://jplat.atlassian.net/browse/BLU-3951
	 *
	 * TODO: when cleaning up 'platform_dst_nav4_skip_link_a11y_1' remove this call entirely
	 */
	useSkipLinkInternal({
		id,
		label: skipLinkLabel,
		isHidden: defaultWidth === 0 || fg('platform_dst_nav4_skip_link_a11y_1'),
	});
	const ref = useRef<HTMLDivElement | null>(null);
	const [width, setWidth] = useState(defaultWidth);
	// Used to track the previous value of the `defaultWidth` prop, for logging dev warnings when it changes.
	const defaultWidthRef = useRef(defaultWidth);

	// `mergeRefs` returns a new callback ref on every call, and combined with a new array literal
	// this would cause React to detach/re-attach the ref on every render. Memoise it so the merged
	// ref is stable across renders.
	const mergedRef = useMemo(
		() => (isMotionUpliftEnabled ? mergeRefs([ref, motionRef]) : ref),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isMotionUpliftEnabled, motionRef],
	);

	/**
	 * TODO: Remove this useEffect once the `width: 0` usage is removed from Jira.
	 * It updates the width state based on changes to `defaultWidth`, as a temporary stopgap to support Jira's current usage.
	 * https://jplat.atlassian.net/browse/BLU-3951
	 */
	useEffect(() => {
		if (defaultWidthRef.current === defaultWidth) {
			return;
		}

		if (isMotionUpliftEnabled) {
			if (defaultWidth === 0) {
				reanimate(Reanimate.exit);
				defaultWidthRef.current = defaultWidth;
				// Set width to 0 once the exit animation is complete
				return;
			} else if (defaultWidthRef.current === 0) {
				reanimate(Reanimate.enter);
			}
		}

		defaultWidthRef.current = defaultWidth;
		setWidth(defaultWidth);

		if (process.env.NODE_ENV !== 'production') {
			// eslint-disable-next-line no-console
			console.warn(
				'Page Layout warning\n\n',
				'The value of the `defaultWidth` prop on the `Panel` layout slot component has changed. This should not be changed after the component has been mounted.\n\n',
				'In the future, changes to the `defaultWidth` prop will not be respected. It is only supported as a stopgap to enable migration from Nav3 to Nav4.\n\n',
			);
		}
	}, [defaultWidth, isMotionUpliftEnabled, reanimate]);

	const sideNavRef = useSideNavRef();

	/**
	 * The minimum width that the panel can be resized to by the user.
	 *
	 * We only use the `defaultWidth` up to a point to avoid huge panels.
	 */
	const minWidth = Math.min(defaultWidth, fallbackResizeMinWidth);

	/**
	 * Returns the bounds for resizing, evaluated lazily when needed.
	 *
	 * Defined separately to the slot bounds because the resizing bounds need to be resolvable to a pixel value,
	 * and the panel's slot bounds use a complex CSS expression.
	 */
	const getResizeBounds = useCallback((): ResizeBounds => {
		const sideNavWidth = sideNavRef.current?.offsetWidth ?? 0;
		/**
		 * By default the panel should not resize larger than the page content, equivalent to the `Main` + `Aside` slots.
		 *
		 * This maximum width is equivalent to half the viewport width, after removing the sidebar width.
		 *
		 * Consumers can opt in to a larger maximum via the `maxWidth` prop.
		 */
		const defaultMaxWidth: ResizeBound = `${Math.round((window.innerWidth - sideNavWidth) / 2)}px`;

		return { min: `${minWidth}px`, max: maxWidth ?? defaultMaxWidth };
	}, [maxWidth, minWidth, sideNavRef]);

	const panelWidthSlotBounds = {
		min: `${minWidth}px`,
		// `sideNavLiveWidthVar` is not defined if the `SideNav` is not mounted, so we fallback to `0px`.
		// Consumers can opt in to a larger maximum via the `maxWidth` prop.
		max: maxWidth ?? `round(nearest, calc((100vw - var(${sideNavLiveWidthVar}, 0px)) / 2), 1px)`,
	};

	const panelVariableWidth = `clamp(${panelWidthSlotBounds.min}, ${width}px, ${panelWidthSlotBounds.max})`;

	useResizingWidthCssVarOnRootElement({
		isEnabled: dangerouslyHoistSlotSizes,
		cssVar: panelSplitterResizingVar,
		panelId: panelPanelSplitterId,
	});

	return (
		<section
			id={id}
			data-layout-slot
			aria-label={label}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
			className={
				isMotionUpliftEnabled
					? // eslint-disable-next-line @atlaskit/ui-styling-standard/local-cx-xcss, @compiled/local-cx-xcss
						cx(
							xcss,
							state === 'hidden' && styles.hidden,
							state === 'entering' && styles.entering,
							state === 'exiting' && styles.exiting,
						)
					: xcss
			}
			css={[
				styles.root,
				!isMotionUpliftEnabled && defaultWidth === 0 && styles.hidden,
				hasBorder && styles.border,
			]}
			style={
				{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values
					[panelVar]: panelVariableWidth,
					'--minWidth': `${minWidth}px`,
				} as CSSProperties
			}
			data-testid={testId}
			ref={mergedRef}
		>
			{dangerouslyHoistSlotSizes && (
				// ------ START UNSAFE STYLES ------
				// These styles are only needed for the UNSAFE legacy use case for Jira + Confluence.
				// When they aren't needed anymore we can delete them wholesale.
				<DangerouslyHoistCssVarToDocumentRoot
					variableName={UNSAFE_panelLayoutVar}
					value="0px"
					mediaQuery={media.above.lg}
					responsiveValue={`var(${panelSplitterResizingVar}, ${panelVariableWidth})`}
				/>
				// ------ END UNSAFE STYLES ------
			)}
			<PanelSplitterProvider
				panelId={panelPanelSplitterId}
				panelRef={ref}
				panelWidth={width}
				onCompleteResize={setWidth}
				getResizeBounds={getResizeBounds}
				resizingCssVar={panelSplitterResizingVar}
				position="start"
			>
				{/**
				 * Overflow scroll styles are added here rather than on the `section` container element, so that the panel splitter
				 * component can overflow out of the `Panel` container, to increase the interactive grab area
				 */}
				<div
					css={[
						styles.scrollContainer,
						isMotionUpliftEnabled && state === 'entering' && styles.contentEntering,
						isMotionUpliftEnabled && state === 'exiting' && styles.contentExiting,
					]}
				>
					{children}
				</div>
			</PanelSplitterProvider>
		</section>
	);
}
